import React, { FC, useState, useEffect, MouseEvent, useRef } from "react";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { View } from "@tarojs/components";

import "./index.scss";
import { XHComponentCommonProps } from "../../types";

export type NumberKeyboardType = "default" | "custom";

export type NumberKeyboardProps = {
  show: boolean;
  type?: NumberKeyboardType;
  showClose?: boolean;
  closeOnClickSpace?: boolean;
  maxLength?: number;
  onBlur?: (e: MouseEvent | Event) => void;
  onClose?: (e: MouseEvent | Event) => void;
  onChange?: (value: string) => void;
  value: string;
  title?: string;
  closeText?: string;
  dataTrackId?: string;
} & XHComponentCommonProps;

export type KeyType = "number" | "delete" | "string" | "none";

export interface IKey {
  text: number | string;
  value: number | string;
  type: KeyType;
}

const NumberKeyboard: FC<NumberKeyboardProps> = props => {
  const {
    onBlur,
    show,
    maxLength = Number.MAX_SAFE_INTEGER,
    showClose = true,
    closeOnClickSpace = true,
    onClose,
    onChange,
    value,
    title = "安全键盘",
    closeText = "收起",
    className,
    dataTrackId = "NumberKeyboard",
  } = props;
  const [input, setInput] = useState(value);
  const keys = getKeys();

  useEffect(() => {
    if (closeOnClickSpace) {
      document.addEventListener("click", handleClose);
    }

    return () => {
      document.removeEventListener("click", handleClose);
    };
  }, []);

  useEffect(() => {
    setInput(value);
  }, [value]);

  function handleClose(e): void {
    onClose && onClose(e);
    onBlur && onBlur(e);
  }

  function handleKeyPress(e, { type, value }: IKey): void {
    if (process.env.TARO_ENV === "h5") {
      e.stopImmediatePropagation();
    }
    if (type === "none") return;

    let newInput = input;

    if (type === "delete") {
      newInput = input.slice(0, input.length - 1);
    } else if (newInput.length < maxLength) {
      newInput = input + value;
    }

    setInput(newInput);

    onChange && onChange(newInput);
  }

  function getKeys(): IKey[] {
    const keys: IKey[] = [];

    for (let i = 1; i <= 9; i++) {
      keys.push({ text: i, value: i, type: "number" });
    }

    keys.push(
      { text: "", value: "", type: "none" },
      { text: 0, value: 0, type: "number" },
      { text: "", value: "", type: "delete" }
    );

    return keys;
  }
  const keyboardcls = classNames("xh-number-keyboard", className);

  const nodeRef = useRef(null);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      classNames="slide-up"
      in={show}
      unmountOnExit
      timeout={200}
    >
      <View
        className={keyboardcls}
        // @ts-ignore
        dataTrackId={dataTrackId}
      >
        <View className="xh-number-keyboard-header">
          <View className="xh-number-keyboard-header-title">{title}</View>
          {showClose && (
            <View
              className="xh-number-keyboard-header-close"
              onClick={handleClose}
              // @ts-ignore
              dataTrackId={`${dataTrackId}_close`}
            >
              {closeText}
            </View>
          )}
        </View>
        <View className="xh-number-keyboard-body">
          {keys.map((key, index) => (
            <View
              key={index}
              // @ts-ignore
              dataTrackId={`${dataTrackId}_key_${index}`}
              className={classNames("xh-number-keyboard-body-key", {
                none: key.type === "none",
                delete: key.type === "delete",
              })}
              onClick={e => {
                handleKeyPress(e, key);
              }}
            >
              {key.text}
            </View>
          ))}
        </View>
      </View>
    </CSSTransition>
  );
};

export default NumberKeyboard;
